// One message from the draft room, applied to the view this client holds
// (REALTIME.md "The rule every event obeys").
//
// ASSIGNMENT, NEVER COMPUTATION. Every event carries the new value of each
// `DraftView` key it changes, so this sets keys and drops or restores one
// row; it knows no draft rules, no pick order and no roster shape. The one
// derived key is the exempt one: a board cell is current when its `overall`
// is `current.overall`. `live:walk` holds a copy built this way to a fresh
// snapshot.

import type { DraftMine, DraftServerMessage, DraftView } from '@fsp/domain';

function withMine(view: DraftView, mine: DraftMine): DraftView {
  const queued = new Set(mine.queuedIds);
  const fills = new Set(mine.fillsNeedIds);
  return {
    ...view,
    myRoster: mine.myRoster,
    myQueue: mine.myQueue,
    myNeeds: mine.myNeeds,
    available: view.available.map((r) =>
      r.queued === queued.has(r.player.id) && r.fillsNeed === fills.has(r.player.id)
        ? r
        : { ...r, queued: queued.has(r.player.id), fillsNeed: fills.has(r.player.id) },
    ),
  };
}

function currentCells(view: DraftView): DraftView {
  const at = view.current?.overall ?? null;
  return {
    ...view,
    board: view.board.map((c) =>
      c.isCurrent === (c.overall === at) ? c : { ...c, isCurrent: c.overall === at },
    ),
  };
}

/** The view after one message. A snapshot replaces it; anything else changes the keys it carries. */
export function applyDraftMessage(view: DraftView | null, m: DraftServerMessage): DraftView | null {
  if (m.type === 'snapshot') return m.payload.view;
  if (view === null) return null;
  const at = { ...view, updatedAt: m.at };
  switch (m.type) {
    case 'pong':
    case 'intent.rejected':
      return view;
    case 'draft.room':
      return { ...at, roomOpen: m.payload.roomOpen, scheduledAt: m.payload.scheduledAt };
    case 'draft.started':
    case 'draft.paused':
    case 'draft.resumed':
      return currentCells({ ...at, status: m.payload.status, current: m.payload.current });
    case 'draft.turn':
      return currentCells({ ...at, current: m.payload.current, upcoming: m.payload.upcoming });
    case 'draft.clock':
      return currentCells({
        ...at,
        pickSeconds: m.payload.pickSeconds,
        current: m.payload.current,
      });
    case 'draft.picked':
    case 'draft.undone': {
      const cell = m.payload.cell;
      let available = at.available;
      if (m.type === 'draft.picked') {
        available = available.filter((r) => r.player.id !== m.payload.removedPlayerId);
      } else if (!available.some((r) => r.player.id === m.payload.restoredPlayer.player.id)) {
        available = [...available, m.payload.restoredPlayer].sort((a, b) => a.rank - b.rank);
      }
      const next: DraftView = {
        ...at,
        board: at.board.map((c) => (c.overall === cell.overall ? cell : c)),
        available,
        current: m.payload.current,
        upcoming: m.payload.upcoming,
        lastPick: m.payload.lastPick,
      };
      return currentCells(withMine(next, m.payload.mine));
    }
    case 'draft.seat':
      return {
        ...at,
        seats: at.seats.map((s) => (s.team.id === m.payload.seat.team.id ? m.payload.seat : s)),
      };
    case 'draft.queue':
      return withMine(at, m.payload.mine);
    case 'draft.completed':
      return currentCells({ ...at, status: m.payload.status, current: null, upcoming: [] });
    case 'draft.traded':
    case 'draft.trade.undone': {
      const cells = new Map(m.payload.cells.map((c) => [c.overall, c]));
      const next: DraftView = {
        ...at,
        board: at.board.map((c) => cells.get(c.overall) ?? c),
        current: m.payload.current,
        upcoming: m.payload.upcoming,
        lastPick: m.payload.lastPick,
        trading: m.payload.trading,
      };
      return currentCells(withMine(next, m.payload.mine));
    }
    case 'draft.offers':
      return { ...at, trading: m.payload.trading };
    // AN AUCTION'S (AUCTION.md §11): the block as it stands, and a sale.
    case 'draft.block':
      return { ...at, auction: m.payload.auction };
    case 'draft.sold': {
      const cell = m.payload.cell;
      const next: DraftView = {
        ...at,
        board: at.board.map((c) => (c.overall === cell.overall ? cell : c)),
        available: at.available.filter((r) => r.player.id !== m.payload.removedPlayerId),
        lastPick: m.payload.lastPick,
        auction: m.payload.auction,
      };
      return withMine(next, m.payload.mine);
    }
  }
}
